import type { Edulead } from 'edulead';
import { enumLabel } from '../services/edulead-v4';

/**
 * An institution of the Go API as the institution components read it: the field names, enum
 * spellings and nesting of the legacy search index document (`/v2/institutions/search`'s
 * `data[]` and `/v2/institutions/:id`'s `data`). CustomList, TableRowInstitution,
 * InstitutionTopCard and GradeSpan are shared with people search and the maps, so the API's
 * shape is translated here, once, rather than in each of them.
 */
export interface LegacyInstitution {
  id: string;
  name: string;
  type: string;
  subtype: string;
  access: string;
  model?: string;
  religious_affiliation?: string;
  id_nces?: string;
  id_nces_school?: string;
  id_nces_district?: string;
  loc_address_phys?: string;
  loc_city?: string;
  loc_state?: string;
  loc_zip?: string;
  loc_county_id?: string;
  loc_county_name?: string;
  phone?: string;
  fax?: string;
  website?: { url: string };
  nr_students: number;
  nr_staff: number;
  nr_child_institutions: number;
  revenue_total: number;
  tuition_annual: number;
  title_one: boolean;
  is_charter?: boolean;
  charter_lea?: string;
  parent?: { id: string; name: string; type: string; id_nces_district?: string };
  email_formats: {
    domain: string;
    domainTotal: number;
    formats: { emailFormat: string; contactCount: number; percentage: number; rank: number }[];
  }[];
  fulltextMatchedUrls?: { url: string; snippet: string }[];
  /** The grade flags, `grade_pk` … `grade_13`, `grade_ug`, `grade_ae`: true for each grade served. */
  [grade: `grade_${string}`]: boolean | undefined;
}

/** A search response in the legacy envelope CustomList reads (`data.data`, `data.total`). */
export interface LegacyInstitutionsResponse {
  data: { data: LegacyInstitution[]; total: number };
}

const optional = (s: string | undefined) => s || undefined;

/** An int64 of the API (a JSON string) as the number the components format. */
const int = (s: string | undefined) => Number(s ?? 0);

export function toLegacyInstitution(v: Edulead.EduleadV4Institution): LegacyInstitution {
  const out: LegacyInstitution = {
    id: v.id ?? '',
    name: v.name ?? '',
    type: enumLabel(v.type, 'INSTITUTION_TYPE_'),
    subtype: enumLabel(v.subtype, 'INSTITUTION_SUBTYPE_'),
    access: enumLabel(v.access, 'INSTITUTION_ACCESS_'),
    model: optional(v.model),
    religious_affiliation: optional(v.religious_affiliation),
    id_nces: optional(v.nces_id),
    id_nces_school: optional(v.nces_school_id),
    id_nces_district: optional(v.nces_district_id),
    loc_address_phys: optional(v.address),
    loc_city: optional(v.city),
    loc_state: optional(v.state),
    loc_zip: optional(v.zip),
    loc_county_id: optional(v.county_id),
    loc_county_name: optional(v.county_name),
    phone: optional(v.phone),
    fax: optional(v.fax),
    website: v.website_url ? { url: v.website_url } : undefined,
    nr_students: int(v.student_count),
    nr_staff: int(v.staff_count),
    nr_child_institutions: int(v.child_count),
    revenue_total: int(v.revenue_usd),
    tuition_annual: int(v.tuition_usd),
    title_one: v.title_one ?? false,
    is_charter: v.charter ?? undefined,
    charter_lea: optional(v.charter_lea),
    parent: v.parent?.id
      ? {
          id: v.parent.id,
          name: v.parent.name ?? '',
          type: enumLabel(v.parent.type, 'INSTITUTION_TYPE_'),
          id_nces_district: optional(v.parent.nces_district_id),
        }
      : undefined,
    email_formats: (v.email_domains ?? []).map((d) => ({
      domain: d.domain ?? '',
      domainTotal: d.domain_total ?? 0,
      formats: (d.formats ?? []).map((f) => ({
        emailFormat: f.email_format ?? '',
        contactCount: f.contact_count ?? 0,
        percentage: f.percentage ?? 0,
        rank: f.rank ?? 0,
      })),
    })),
    fulltextMatchedUrls: v.fulltext_matches?.length
      ? v.fulltext_matches.map((m) => ({ url: m.url ?? '', snippet: m.snippet ?? '' }))
      : undefined,
  };
  for (const grade of v.grades ?? []) {
    out[`grade_${grade.toLowerCase()}`] = true;
  }
  return out;
}

/** A page of search results, and the total over every page, in the legacy envelope. */
export function legacyInstitutionsResponse(
  items: Edulead.EduleadV4Institution[],
  totalSize: string | undefined,
): LegacyInstitutionsResponse {
  return { data: { data: items.map(toLegacyInstitution), total: int(totalSize) } };
}

/**
 * The Go API's order for the console's sort (the URL's `sortBy` and `sortDir`): name or the
 * student count, the two sortable columns; anything else is by name.
 */
export function institutionOrder(sortBy: string | undefined, sortDir: string | undefined) {
  const desc = sortDir === 'desc' ? ' desc' : '';
  return (sortBy === 'studentsTotal' ? `student_count${desc}` : `name${desc}`) as
    'name' | 'name desc' | 'student_count' | 'student_count desc';
}
